<template>
  <div class="join-widget text-white py-12 px-4 md:px-8">
    <!-- Step 6+7: Existing Customers Message -->
    <p class="text-center text-font-primary text-[16px] mb-6">
      If you are an existing OnePass mobile customer and would like to choose a
      different plan, login to
      <a href="/account/dashboard" class="text-primary underline font-bold"
        >OnePass mobile Dashboard</a
      >
      or the OnePass mobile app. No new SIM card is required.
    </p>

    <!-- Main Purple Section -->
    <div class="relative flex flex-col items-center">
      <div
        class="bg-[#9900f1] w-full max-w-[1363px] rounded-lg flex flex-col justify-center items-center text-white px-8 pt-10 pb-32"
      >
        <h2
          class="text-white text-[32px] font-extrabold text-center uppercase mb-12"
        >
          {{ content.sectionTitle }}
        </h2>

        <!-- Benefit Tiles -->
        <div
          class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 w-full max-w-5xl"
        >
          <div
            v-for="(tile, index) in content.benefitTiles"
            :key="index"
            class="bg-white rounded-lg p-6 text-center flex flex-col items-center"
          >
            <img
              class="h-[50px] w-[65px] mt-6"
              :src="useStrapiMedia(tile.image?.url)"
              :alt="tile.content"
            />
            <p class="text-font-primary text-[20px] font-[500] my-4">
              {{ tile.content }}
            </p>
          </div>
        </div>
        <div class="p-16"></div>
      </div>

      <!-- Lifted Download App Box -->
      <div
        class="bg-[#3a016a] p-8 shadow-xl rounded-lg w-[90%] md:w-[620px] mx-auto absolute bottom-[-80px] md:bottom-[-50px] flex flex-col items-center"
      >
        <h3
          class="text-white text-center text-[20px] md:text-[24px] font-bold leading-snug mb-4"
        >
          {{ content.appTitle }} <br />
          {{ content.appSubTitle }}
        </h3>

        <div class="flex justify-center gap-4">
          <a
            :href="content.appStoreLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              :src="useStrapiMedia(content.appImage?.url)"
              alt="App Store"
              class="w-[199px] h-[64px]"
            />
          </a>
          <a
            :href="content.googlePlayLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              :src="useStrapiMedia(content.googlePlayImage?.url)"
              alt="Google Play"
              class="w-[227px] h-[64px]"
            />
          </a>
        </div>
      </div>
    </div>

    <!-- Steps and App Screenshot Section -->
    <div
      class="join-widget__steps grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center mt-32 pt-12"
    >
      <div class="space-y-6">
        <div class="prose prose-invert" v-html="renderedStepsSection"></div>
        <button
          class="bg-[#9900f1] hover:bg-[#7a00cc] text-white font-bold py-3 px-16 rounded-full w-full md:w-auto"
          @click="navigateToActivate"
        >
          {{ content.buttonText }}
        </button>
        <div class="text-sm mt-4" v-html="renderedBottomLink"></div>
      </div>
      <div class="flex justify-center md:justify-end">
        <img
          :src="useStrapiMedia(content.appScreenshot?.url)"
          alt="App Screenshot"
          class="phone-image"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import MarkdownIt from "markdown-it";
import { useRouter } from "vue-router";

const router = useRouter();
const md = new MarkdownIt({ html: true, linkify: true, breaks: true });

const props = defineProps({
  content: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const renderedStepsSection = computed(() => {
  let cleanContent =
    props.content.stepsSection
      ?.split("\n")
      .map((line) => line.trim())
      .join("\n") || "";

  return md.render(
    cleanContent.replace(
      /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
      (_, phone) => `[${phone}](tel:${phone.replace(/\s+/g, "")})`
    )
  );
});

const renderedBottomLink = computed(() =>
  md.render(props.content.bottomLink || "")
);

const navigateToActivate = () => {
  router.push("/sim-activation");
};
</script>

<style scoped>
.join-widget__steps :deep(h3) {
  @apply uppercase text-primary text-[24px] font-extrabold mt-4;
}

.join-widget__steps :deep(ol) {
  @apply list-decimal text-font-primary;
}

.join-widget__steps :deep(p) {
  @apply text-font-primary;
}

.join-widget__steps :deep(a) {
  @apply text-primary;
}

.phone-image {
  width: 546px;
  height: 491px;
  transform: none !important;
  object-fit: contain;
}
</style>
